// Ein Schritt der Simulation: wendet die Absicht dieses Ticks auf den Spielzustand an. Läuft in
// festen Schritten (TICK_SECONDS), unabhängig von der Bildrate, ohne DOM und ohne Three.js, damit
// derselbe Code später auf dem Server läuft (Architektur-Regel 2). Bei gleichen Eingaben ist das
// Ergebnis deterministisch.

import { ANIMAL_BEHAVIOR, animalAabb, stepAnimal, type AnimalState } from '../animals/index.js';
import { availableActions, findTarget, performAction } from '../interaction/index.js';
import { clamp } from '../util/math.js';
import { worldBounds, type World } from '../world/chunk.js';
import type { Pen } from '../world/types.js';
import type { Intent } from './intents.js';
import { aabbAt, aabbsOverlap, moveAabb, separationDelta } from './physics.js';
import { PLAYER, TICK_SECONDS, type GameState, type PlayerState } from './state.js';

/** Der Blick ist auf ±89° begrenzt, damit die Kamera nie über den Scheitel kippt. */
const MAX_PITCH = (89 * Math.PI) / 180;

/** Nicht-endliche Zahlen (NaN, ±Unendlich) zählen als 0. */
function endlich(wert: number): number {
  return Number.isFinite(wert) ? wert : 0;
}

/** Bewegungsrichtung auf -1, 0 oder 1 gebracht, was auch immer zur Laufzeit ankommt. */
function richtung(wert: number): -1 | 0 | 1 {
  const w = endlich(wert);
  if (w > 0) return 1;
  if (w < 0) return -1;
  return 0;
}

/**
 * Die Absicht mit bereinigten Zahlen (ADR 0007). Der Typ verspricht gültige Werte, doch später
 * kommen Absichten vom Client über das Netz beim Server an (Architektur-Regel 3); ein einziges NaN
 * würde Position und Blick sonst dauerhaft vergiften, weil clamp und Math.floor es durchreichen.
 */
function bereinigt(intent: Intent): Intent {
  return {
    ...intent,
    forward: richtung(intent.forward),
    strafe: richtung(intent.strafe),
    lookYaw: endlich(intent.lookYaw),
    lookPitch: endlich(intent.lookPitch),
  };
}

/**
 * Waagerechte Geschwindigkeit aus Blickrichtung und Absicht. `yaw = 0` blickt nach -z, positive
 * Werte drehen nach links; `strafe = 1` geht nach rechts. Schräg (vorwärts plus seitwärts) wird
 * auf `speed` normiert, damit niemand diagonal schneller ist.
 */
function waagerechteGeschwindigkeit(player: PlayerState, intent: Intent, speed: number): void {
  const vorX = -Math.sin(player.yaw);
  const vorZ = -Math.cos(player.yaw);
  const rechtsX = Math.cos(player.yaw);
  const rechtsZ = -Math.sin(player.yaw);
  const x = vorX * intent.forward + rechtsX * intent.strafe;
  const z = vorZ * intent.forward + rechtsZ * intent.strafe;
  const laenge = Math.hypot(x, z);
  if (laenge === 0) {
    player.vel.x = 0;
    player.vel.z = 0;
    return;
  }
  player.vel.x = (x / laenge) * speed;
  player.vel.z = (z / laenge) * speed;
}

/**
 * Hält den Fußpunkt in der Welt: seitlich mit halber Breite Abstand zur Kante, `y` in
 * `[0, height]`. Die Unterkante zählt als Boden (siehe ADR 0005), damit auch eine Welt ohne
 * festen Block unter den Füßen niemanden ins Bodenlose fallen lässt.
 */
function anWeltgrenzenHalten(player: PlayerState, world: World): void {
  const grenzen = worldBounds(world);
  const halbeBreite = PLAYER.width / 2;
  player.pos.x = clamp(player.pos.x, grenzen.minX + halbeBreite, grenzen.maxX - halbeBreite);
  player.pos.z = clamp(player.pos.z, grenzen.minZ + halbeBreite, grenzen.maxZ - halbeBreite);
  if (player.pos.y <= 0) {
    player.pos.y = 0;
    player.vel.y = 0;
    player.onGround = true;
  } else if (player.pos.y > grenzen.height) {
    player.pos.y = grenzen.height;
    player.vel.y = 0;
  }
}

/**
 * Tiere, die dem Avatar nicht (ganz) ausweichen konnten, halten ihn an: Überlappt sein Quader
 * nach dem Tierschritt noch ein Tier, wird er entlang der Achse mit der geringsten Durchdringung
 * vom Tier weg gedrückt, über `moveAabb` gegen feste Blöcke wie jede Bewegung; die Geschwindigkeit
 * auf dieser Achse wird genullt wie bei einem Blocktreffer. So kommt er nie durch ein Tier
 * hindurch, und wer von oben auf ein Tier sinkt, bleibt auf seinem Rücken (ADR 0018).
 */
function anTierenAnhalten(player: PlayerState, animals: readonly AnimalState[], world: World): void {
  const hw = PLAYER.width / 2;
  for (const animal of animals) {
    const avatar = aabbAt(player.pos, hw, PLAYER.height);
    const tier = animalAabb(animal);
    if (!aabbsOverlap(avatar, tier)) continue;
    const delta = separationDelta(avatar, tier, ANIMAL_BEHAVIOR.pushMargin);
    player.pos = moveAabb(world, player.pos, hw, PLAYER.height, delta).pos;
    if (delta.x !== 0) player.vel.x = 0;
    if (delta.y !== 0) player.vel.y = 0;
    if (delta.z !== 0) player.vel.z = 0;
  }
}

/** Was ein Simulationsschritt außer der Absicht noch wissen will. */
export interface StepOptions {
  /**
   * Ob die Tiere in diesem Schritt gehen. Standard wahr. Der Client schaltet sie seit task-013
   * ab: Über die Tiere entscheidet der Server, der Client zeigt nur, was in `farm`-Nachrichten
   * ankommt (ADR 0027). Der Avatar wird trotzdem an ihnen angehalten – das ist Kollision, keine
   * Simulation, und ohne sie liefe man durch die Kuh hindurch.
   */
  animals?: boolean;
}

/**
 * Genau ein Tick; verändert `state`. Reihenfolge: Absicht bereinigen, Blick, Fliegen umschalten,
 * Geschwindigkeit aus der Absicht, Bewegung mit Kollision, Weltgrenzen, Tiere (mit Stopp des
 * Avatars an Tieren, die nicht ausweichen konnten), Interaktion, Tick hochzählen.
 */
export function stepSimulation(state: GameState, intent: Intent, world: World, pens: Pen[], options: StepOptions = {}): void {
  const player = state.player;
  const absicht = bereinigt(intent);

  // Blick: die Absicht enthält die Drehung dieses Ticks, nicht einen Absolutwert.
  player.yaw += absicht.lookYaw;
  player.pitch = clamp(player.pitch + absicht.lookPitch, -MAX_PITCH, MAX_PITCH);

  // Fliegen umschalten: abheben bzw. landen; die senkrechte Bewegung beginnt jeweils bei null.
  if (absicht.toggleFly) {
    player.flying = !player.flying;
    player.vel.y = 0;
  }

  // Geschwindigkeit aus der Absicht.
  waagerechteGeschwindigkeit(player, absicht, player.flying ? PLAYER.flySpeed : PLAYER.walkSpeed);
  if (player.flying) {
    player.vel.y = ((absicht.up ? 1 : 0) - (absicht.down ? 1 : 0)) * PLAYER.flySpeed;
  } else {
    if (absicht.jump && player.onGround) player.vel.y = PLAYER.jumpSpeed;
    player.vel.y -= PLAYER.gravity * TICK_SECONDS;
  }

  // Bewegung mit Kollision gegen das Blockraster; ein Treffer nullt den Anteil auf dieser Achse.
  const delta = {
    x: player.vel.x * TICK_SECONDS,
    y: player.vel.y * TICK_SECONDS,
    z: player.vel.z * TICK_SECONDS,
  };
  const bewegung = moveAabb(world, player.pos, PLAYER.width / 2, PLAYER.height, delta);
  player.pos = bewegung.pos;
  if (bewegung.hitX) player.vel.x = 0;
  if (bewegung.hitY) player.vel.y = 0;
  if (bewegung.hitZ) player.vel.z = 0;
  player.onGround = bewegung.hitY && delta.y < 0;

  anWeltgrenzenHalten(player, world);

  // Tiere: je Tier ein Schritt mit seiner Koppel. Ein Tier ohne passende Koppel ist ein Fehler im
  // Dorf-Bauplan und fällt hier auf, statt still stehen zu bleiben.
  if (options.animals ?? true) {
    for (const animal of state.animals) {
      const pen = pens.find((p) => p.id === animal.penId);
      if (pen === undefined) {
        const bekannt = pens.map((p) => p.id).join(', ') || '(keine)';
        throw new Error(`Tier „${animal.id}“ steht in der Koppel „${animal.penId}“, die es nicht gibt. Bekannt: ${bekannt}.`);
      }
      stepAnimal(animal, state, world, pen);
    }
  }
  anTierenAnhalten(player, state.animals, world);
  anWeltgrenzenHalten(player, world);

  // Interaktion: bei `interact` genau einmal die erste Handlung am Ziel im Blick (Architektur-
  // Regel 5). Bleibt die Taste gedrückt, passiert nichts weiter: Nach dem Melken ist die Kuh leer,
  // die Liste ist also leer, bis das Produkt wieder bereit ist (ADR 0009).
  if (absicht.interact) {
    const [handlung] = availableActions(state, findTarget(state));
    if (handlung !== undefined) performAction(state, handlung);
  }

  state.tick += 1;
}
